'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { Wifi, WifiOff, Clock, RefreshCw, Database } from 'lucide-react';
import type { SurveyRecord } from '@/types/survey';
import type { SurveyStatus } from '@/components/ui/StatusBadge';
import StatsPanel from './StatsPanel';
import StatusDistributionChart from './StatusDistributionChart';
import SurveyTableClient from './SurveyTableClient';
import UrlParamsBanner from './UrlParamsBanner';
import { surveyService } from '@/lib/services/surveyService';

const VALID_STATUSES: SurveyStatus[] = ['complete', 'terminate', 'qualitySecurity', 'quotaFull'];

function isValidStatus(s: string | null): s is SurveyStatus {
  return !!s && (VALID_STATUSES as string[]).includes(s);
}

interface DashboardClientProps {
  isAdmin: boolean;
}

export default function DashboardClient({ isAdmin }: DashboardClientProps) {
  const searchParams = useSearchParams();
  const [records, setRecords] = useState<SurveyRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [detectedIp, setDetectedIp] = useState<string | null>(null);
  const [isCapturingIp, setIsCapturingIp] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const pidParam = searchParams.get('pid');
  const uidParam = searchParams.get('uid');
  const statusParam = searchParams.get('status');

  // ─── Initial load from Supabase ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await surveyService.getAll();
        if (!cancelled) {
          setRecords(data);
          const now = new Date();
          setLastUpdated(
            `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
          );
        }
      } catch (err) {
        console.log('Failed to load survey records:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ─── Fetch IP from ipify ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!pidParam && !uidParam && !statusParam) return;
    setIsCapturingIp(true);
    fetch('https://api.ipify.org?format=json')
      .then((r) => r.json())
      .then((data: { ip: string }) => {
        setDetectedIp(data.ip);
        setIsCapturingIp(false);
      })
      .catch(() => {
        setDetectedIp('0.0.0.0');
        setIsCapturingIp(false);
      });
  }, [pidParam, uidParam, statusParam]);

  // ─── Append new record from URL params once IP is resolved ───────────────────
  useEffect(() => {
    if (!pidParam || !uidParam || !isValidStatus(statusParam)) return;
    if (isCapturingIp) return;
    if (detectedIp === null && (pidParam || uidParam)) return;

    const ip = detectedIp ?? '0.0.0.0';
    const now = new Date();
    const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

    const saveRecord = async () => {
      // Check if already saved (prevent duplicate on re-render)
      const alreadyInState = records.some(
        (r) => r.pid === pidParam && r.username === uidParam
      );
      if (alreadyInState) return;

      const newRecord: Omit<SurveyRecord, 'id' | 'isNew' | 'isExiting'> = {
        pid: pidParam,
        username: uidParam,
        status: statusParam as SurveyStatus,
        ip,
        timestamp: ts,
      };

      // Optimistic UI update
      const tempId = `temp-${pidParam}-${uidParam}-${Date.now()}`;
      const optimistic: SurveyRecord = { ...newRecord, id: tempId, isNew: true };
      setRecords((prev) => [optimistic, ...prev]);

      // Persist to Supabase
      try {
        const saved = await surveyService.create(newRecord);
        if (saved) {
          setRecords((prev) =>
            prev.map((r) => (r.id === tempId ? { ...saved, isNew: true } : r))
          );
          setTimeout(() => {
            setRecords((prev) =>
              prev.map((r) => (r.id === saved.id ? { ...r, isNew: false } : r))
            );
          }, 1500);
        } else {
          // Remove optimistic record if save failed
          setRecords((prev) => prev.filter((r) => r.id !== tempId));
        }
      } catch {
        setRecords((prev) => prev.filter((r) => r.id !== tempId));
      }
    };

    saveRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedIp, isCapturingIp, pidParam, uidParam, statusParam]);

  // ─── Delete ───────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (id: string) => {
    setRecords((prev) =>
      prev.map((r) => (r.id === id ? { ...r, isExiting: true } : r))
    );
    setTimeout(async () => {
      setRecords((prev) => prev.filter((r) => r.id !== id));
      try {
        await surveyService.delete(id);
      } catch (err) {
        console.log('Delete failed:', err);
      }
    }, 320);
  }, []);

  // ─── Edit ─────────────────────────────────────────────────────────────────────
  const handleEdit = useCallback(async (updated: SurveyRecord) => {
    setRecords((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    try {
      await surveyService.update(updated);
    } catch (err) {
      console.log('Update failed:', err);
    }
  }, []);

  // ─── Refresh ──────────────────────────────────────────────────────────────────
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const data = await surveyService.getAll();
      setRecords(data);
      const now = new Date();
      setLastUpdated(
        `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
      );
    } catch (err) {
      console.log('Refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 fade-in">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
            Survey Response Monitor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time status tracking for all survey project redirects
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* IP status indicator */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted border border-border">
            {detectedIp ? (
              <>
                <Wifi size={13} className="text-green-400" />
                <span className="text-xs font-mono text-muted-foreground tabular-nums">{detectedIp}</span>
              </>
            ) : (
              <>
                <WifiOff size={13} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">No IP detected</span>
              </>
            )}
          </div>

          {/* Last updated */}
          {lastUpdated && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted border border-border">
              <Clock size={13} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground tabular-nums">Updated {lastUpdated}</span>
            </div>
          )}

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 transition-all duration-150 active:scale-95 disabled:opacity-60"
          >
            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* URL params banner */}
      <UrlParamsBanner
        pid={pidParam}
        uid={uidParam}
        status={statusParam}
        ip={detectedIp}
        isCapturing={isCapturingIp}
      />

      {/* Stats panel */}
      <StatsPanel records={records} />

      {/* Chart + Role badge row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-4 fade-in-up fade-in-delay-5">
        <div className="lg:col-span-2 xl:col-span-3">
          <StatusDistributionChart records={records} />
        </div>
        <div className="glass-card rounded-xl border border-border p-5 flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">Access Level</h3>
            <p className="text-xs text-muted-foreground">Your current permissions for this session</p>
          </div>
          <div className="mt-4 space-y-3">
            <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border ${isAdmin ? 'bg-primary/10 border-primary/25' : 'bg-muted border-border'}`}>
              <Database size={15} className={isAdmin ? 'text-primary' : 'text-muted-foreground'} />
              <div>
                <p className={`text-sm font-semibold ${isAdmin ? 'text-primary' : 'text-foreground'}`}>
                  {isAdmin ? 'Administrator' : 'Staff (View Only)'}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isAdmin ? 'Full edit and delete access' : 'Read-only access to all records'}
                </p>
              </div>
            </div>
            <div className="space-y-1.5">
              {[
                { label: 'View records', allowed: true },
                { label: 'Filter & search', allowed: true },
                { label: 'Edit records', allowed: isAdmin },
                { label: 'Delete records', allowed: isAdmin },
                { label: 'Bulk operations', allowed: isAdmin },
              ].map((perm) => (
                <div key={`perm-${perm.label.replace(/ /g, '-')}`} className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{perm.label}</span>
                  <span className={`text-xs font-medium ${perm.allowed ? 'text-green-400' : 'text-red-400/70'}`}>
                    {perm.allowed ? '✓' : '✗'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Table section */}
      <div className="fade-in-up" style={{ animationDelay: '0.3s', opacity: 0, animation: 'fadeInUp 0.5s ease 0.3s forwards' }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-foreground">Survey Records</h2>
          <span className="text-xs text-muted-foreground tabular-nums">
            {records.length} total entries
          </span>
        </div>
        <SurveyTableClient
          records={records}
          isAdmin={isAdmin}
          isLoading={isLoading}
          onDelete={handleDelete}
          onEdit={handleEdit}
        />
      </div>
    </div>
  );
}